import { NavLink, useNavigate } from "react-router-dom";
import { usePortalAuth } from "./PortalAuthContext";
import { Inbox, FileSignature, Receipt, LogOut, Home, FileText } from "lucide-react";
import PortalNotificationsBell from "./PortalNotificationsBell";

// v68.14 — Portal client shell, "Steel & Signal" refresh.
const NAV = [
  { to: "/portal", label: "Dashboard", icon: <Home size={14} />, end: true, testid: "portal-nav-home" },
  { to: "/portal/files", label: "File requests", icon: <Inbox size={14} />, testid: "portal-nav-files" },
  { to: "/portal/signatures", label: "Signatures", icon: <FileSignature size={14} />, testid: "portal-nav-signatures" },
  { to: "/portal/documents", label: "My documents", icon: <FileText size={14} />, testid: "portal-nav-documents" },
  { to: "/portal/invoices", label: "Invoices", icon: <Receipt size={14} />, testid: "portal-nav-invoices" },
];

export default function PortalLayout({ children }) {
  const { state, logout } = usePortalAuth();
  const navigate = useNavigate();
  const onLogout = async () => { await logout(); navigate("/portal/login"); };

  const orgName = state?.org?.name || "Portal";
  const initials = orgName.slice(0, 2).toUpperCase();

  return (
    <div className="min-h-screen flex" style={{ background: "var(--canvas)" }}>
      <aside
        className="w-72 border-r border-[var(--border)] flex flex-col py-8 px-5 sticky top-0 h-screen"
        style={{ background: "var(--paper)" }}
        data-testid="portal-client-sidebar"
      >
        {/* Brand mark */}
        <div className="mb-10 flex items-center gap-3 px-2">
          <div className="w-11 h-11 rounded-lg bg-[var(--ink)] text-[var(--paper)] flex items-center justify-center font-heading text-lg shrink-0"
               style={{ fontWeight: 500 }} data-testid="portal-brand-mark">
            {initials}
          </div>
          <div className="min-w-0">
            <div className="overline text-[9px] mb-0.5">Client portal</div>
            <div className="font-heading text-[16px] leading-tight truncate" style={{ fontWeight: 500 }}>
              {orgName}
            </div>
          </div>
        </div>

        {/* Account label */}
        <div className="px-3 py-3 mb-6 rounded-md" style={{ background: "var(--subtle)" }}>
          <div className="text-[9px] uppercase tracking-wider text-[var(--ink-3)] mb-0.5">Account</div>
          <div className="text-[13px] font-medium truncate text-[var(--ink)]" data-testid="portal-account-name">
            {state?.client?.name || "—"}
          </div>
        </div>

        {/* Nav */}
        <nav className="flex-1 space-y-0.5">
          {NAV.map((n) => (
            <NavLink
              key={n.to}
              to={n.to}
              end={n.end}
              data-testid={n.testid}
              className={({ isActive }) =>
                `flex items-center gap-3 px-3 py-2.5 rounded-md text-[13px] transition ${
                  isActive
                    ? "bg-[var(--ink)] text-[var(--paper)]"
                    : "text-[var(--ink-2)] hover:text-[var(--ink)] hover:bg-[var(--subtle)]"
                }`
              }
            >
              {n.icon} <span className="flex-1">{n.label}</span>
            </NavLink>
          ))}
          <div className="pt-2 mt-2 border-t border-[var(--border)]">
            <PortalNotificationsBell />
          </div>
        </nav>

        {/* Footer */}
        <div className="border-t border-[var(--border)] pt-4 mt-4 space-y-1">
          <div className="text-[11px] text-[var(--ink-3)] px-3 mb-1 truncate" data-testid="portal-user-email">
            {state?.client_user?.email}
          </div>
          <button
            onClick={onLogout}
            data-testid="portal-logout"
            className="w-full flex items-center gap-2 px-3 py-2 rounded-md text-[12px] text-[var(--ink-2)] hover:text-[var(--accent)] hover:bg-[var(--subtle)] transition"
          >
            <LogOut size={13} /> Log out
          </button>
          <div className="text-[10px] text-[var(--ink-3)] px-3 pt-2 opacity-60">
            Secured by Practice OS
          </div>
        </div>
      </aside>

      <main className="flex-1 overflow-x-hidden">{children}</main>
    </div>
  );
}
